// src/components/patient/overview/NextActionWidget.tsx
//
// Overview v3 (OV-08): the backend's next action names a chart section (`action.tab`, the same keys
// as alert deep links) or an inline step (`inline: 'ACTIVATE_TRACK'`). It never navigates with the
// router: the old `/patients/{id}/…` routes do not exist and sent the user out of the chart.
import { useState } from 'react';
import { ArrowRight, AlertCircle, Loader } from 'lucide-react';
import PrimaryButton from '../../common/PrimaryButton';
import type { NextAction } from '../../../dtos/patientmr/certificationPeriod.dto';
import { ServiceTracksService } from '../../../services/patientmr/serviceTracks.service';
import { alertTargetTab } from './alertActions';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import type { PermissionString } from '../../../types/permissions';

/** The activate route's permissions (service-tracks/activate) */
const ACTIVATE_PERMS = ['patientmr:phi:service-track:update', 'patientmr:phi:service-track:updateall'] as PermissionString[];

interface NextActionWidgetProps {
  patientId:       string;
  nextAction:      NextAction | null | undefined;
  /** Opens a chart section by tab name */
  onNavigateTab?:  (tab: string) => void;
  /** Called after an inline step succeeds (reload the dashboard) */
  onDone:          () => void;
  readOnly?:       boolean;
}

const PRIORITY_STYLES: Record<string, string> = {
  HIGH:   'bg-red-100 text-red-700',
  MEDIUM: 'bg-amber-100 text-amber-800',
  LOW:    'bg-slate-100 text-slate-600',
};
const PRIORITY_LABEL: Record<string, string> = { HIGH: 'Needed now', MEDIUM: 'Soon', LOW: 'Later' };

export default function NextActionWidget({ patientId, nextAction, onNavigateTab, onDone, readOnly = false }: NextActionWidgetProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mayActivate = useHasAnyPermission(ACTIVATE_PERMS);

  if (!nextAction) return null;

  const tab = nextAction.action?.tab ? alertTargetTab({ action: { tab: nextAction.action.tab } }) : null;
  const canActivate = !readOnly && mayActivate && nextAction.inline === 'ACTIVATE_TRACK' && Boolean(nextAction.discipline);
  const canOpen = Boolean(tab && onNavigateTab);

  const activate = async () => {
    if (!nextAction.discipline) return;
    setBusy(true);
    setError(null);
    try {
      await ServiceTracksService.activate(patientId, { discipline: nextAction.discipline });
      onDone();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not start the service');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="next-action">
      <div className="px-4 py-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-slate-500 mb-0.5">Next step</p>
          <h3 className="text-sm font-semibold text-slate-900">{nextAction.label}</h3>
          <p className="text-xs text-slate-600 mt-0.5">{nextAction.description}</p>
        </div>
        <span className={`shrink-0 mt-0.5 text-xs font-medium px-2 py-0.5 rounded-full ${PRIORITY_STYLES[nextAction.priority] ?? PRIORITY_STYLES.LOW}`}>
          {PRIORITY_LABEL[nextAction.priority] ?? nextAction.priority}
        </span>
      </div>

      {error && (
        <div className="mx-4 mb-3 flex items-start space-x-2 p-2 bg-red-50 border border-red-200 rounded-md" role="alert">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {(canActivate || canOpen) && (
        <div className="px-4 pb-3">
          {canActivate ? (
            <PrimaryButton onClick={activate} disabled={busy} className="w-full flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed">
              {busy ? (<><Loader className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />Starting…</>) : nextAction.label}
            </PrimaryButton>
          ) : (
            <PrimaryButton onClick={() => onNavigateTab?.(tab as string)} className="w-full flex items-center justify-center">
              Open {tab}
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </PrimaryButton>
          )}
        </div>
      )}
    </div>
  );
}
